import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { RestService } from '@cumlaude/shared-services';
import { toSignal } from '@angular/core/rxjs-interop';
import { EtlService } from '../../services/etl.service';
import { TabMenuComponent } from '@cumlaude/shared-components-menu';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { BehaviorSubject, catchError, filter, firstValueFrom, map, of, switchMap, timer } from 'rxjs';
import { InstellingBron, InstellingETLStatus, RInstelling, WorkflowTemplate } from '@cumlaude/service-contract';
import { ToastrService } from '@cumlaude/shared-components-toast';
import {
	CheckboxComponent,
	InstantSearchBoxComponent,
	MultiSelectDropdownComponent,
	Option,
	SingleSelectDropdownComponent,
} from '@cumlaude/shared-components-inputs';
import { InstellingenListComponent } from '../../components/instellingen-list/instellingen-list.component';
import { includesIgnoreCaseAndDiacritics } from '@cumlaude/shared-utils';
import { Dialog } from '@angular/cdk/dialog';
import { WorkflowStartDialogComponent, WorkflowStartDialogResult } from '../../dialogs/workflow-start-dialog/workflow-start-dialog.component';

@Component({
	selector: 'app-etl-start',
	templateUrl: './etl-start.component.html',
	styleUrls: ['./etl-start.component.scss'],
	imports: [
		TabMenuComponent,
		ButtonComponent,
		SingleSelectDropdownComponent,
		InstellingenListComponent,
		InstantSearchBoxComponent,
		CheckboxComponent,
		MultiSelectDropdownComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtlStartComponent {
	private readonly restService = inject(RestService);
	private readonly etlService = inject(EtlService);
	protected readonly toastr = inject(ToastrService);
	protected readonly dialog = inject(Dialog);

	available$ = this.restService.isArgoWorkflowsAvailable();
	available = toSignal(this.available$);

	bronnen = Object.values(InstellingBron).map((bron) => new Option<InstellingBron>(bron));
	bron = signal(InstellingBron.Somtoday);
	selectedBronOptie = computed(() => this.bronnen.find((bron) => bron.value === this.bron()));

	etlStatussen = [undefined, ...Object.values(InstellingETLStatus)];
	etlStatusOptions = this.etlStatussen.map((status) => new Option<InstellingETLStatus | undefined>(status, status ?? 'Nog niet gedraaid'));
	deltaLaatsteStatus = signal<(InstellingETLStatus | undefined)[]>(this.etlStatussen);
	bulkLaatsteStatus = signal<(InstellingETLStatus | undefined)[]>(this.etlStatussen);

	searchInput = signal('');

	workflowTemplates = toSignal(
		this.available$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.restService.getWorkflowTemplates().pipe(catchError(() => of([] as WorkflowTemplate[]))))
		),
		{
			initialValue: [],
		}
	);

	templates = computed(() => this.workflowTemplates().map((workflowTemplate) => this.etlService.mapWorkflowTemplate(workflowTemplate)));

	template = computed(() => this.templates().find((template) => template.bron === this.bron()));

	defaultParallelism = toSignal(
		this.available$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.restService.getDefaultParallelism().pipe(catchError(() => of(undefined))))
		)
	);

	private readonly refreshInstellingen$ = new BehaviorSubject<void>(undefined);

	instellingen = toSignal(
		this.refreshInstellingen$.pipe(
			switchMap(() => timer(0, 60 * 1000)),
			switchMap(() => this.restService.getInstellingen()),
			map((instellingen) => instellingen.filter((instelling) => instelling.actief && instelling.etlUitgeschakeld === 0))
		),
		{
			initialValue: [],
		}
	);

	instellingenVanBron = computed(() => this.instellingen().filter((instelling) => instelling.bron === this.bron()));

	filteredInstellingen = computed(() => {
		return this.instellingenVanBron()
			.filter((instelling) => includesIgnoreCaseAndDiacritics(instelling.naam, this.searchInput()))
			.filter((instelling) => {
				const laatsteDeltaStatus = this.deltaLaatsteStatus();
				const deltaETLStatus = instelling.laatsteDeltaJobrun?.instellingETLStatus;
				if (laatsteDeltaStatus.length > 0 && !laatsteDeltaStatus.includes(deltaETLStatus)) return false;

				const laatsteBulkStatus = this.bulkLaatsteStatus();
				const bulkETLStatus = instelling.laatsteBulkJobrun?.instellingETLStatus;
				return !(laatsteBulkStatus.length > 0 && !laatsteBulkStatus.includes(bulkETLStatus));
			});
	});

	checkedInstellingen = linkedSignal<RInstelling[], RInstelling[]>({
		source: this.instellingenVanBron,
		computation: (source, previous) => {
			const ids = new Set(previous?.value.map((instelling) => instelling.id) ?? []);
			return source.filter((instelling) => ids.has(instelling.id));
		},
	});

	hasCheckedInstellingen = computed(() => this.checkedInstellingen().length > 0);

	canStart = computed(() => this.hasCheckedInstellingen() && this.defaultParallelism() !== undefined);

	allInstellingenChecked = computed(() => this.instellingenVanBron().length === this.checkedInstellingen().length);

	async startETLJob() {
		const template = this.template();
		if (!template) return;

		const checkedInstellingen = this.checkedInstellingen();
		if (checkedInstellingen.length === 0) return;

		const defaultParallelism = this.defaultParallelism();
		if (!defaultParallelism) return;

		const dialogRef = this.dialog.open<WorkflowStartDialogResult>(WorkflowStartDialogComponent, {
			data: {
				checkedInstellingen: checkedInstellingen,
				defaultParallelism: this.bron() === InstellingBron.Somtoday ? defaultParallelism.somtoday : defaultParallelism.magister,
			},
		});
		const result = await firstValueFrom(dialogRef.closed);
		if (!result) return;

		const { bulk, parallelism } = result;

		const workflowStarted = await this.etlService.startInstellingenWorkflow(
			template.workflowTemplate.metadata.name,
			bulk,
			this.allInstellingenChecked() ? undefined : checkedInstellingen,
			parallelism
		);

		const etlType = bulk ? 'Bulkverwerking' : 'Deltaverwerking';
		const bron = this.bron();
		const aantalInstellingen = checkedInstellingen.length;
		if (workflowStarted) {
			this.toastr.success(`${etlType} voor ${aantalInstellingen} ${bron} instellingen is gestart.`);
			setTimeout(() => this.refreshInstellingen$.next(), 5 * 1000);
		} else {
			this.toastr.error(`Starten van ${etlType} voor ${aantalInstellingen} ${bron} instellingen is mislukt.`);
		}
	}

	updateChecked(checkedInstelling: RInstelling) {
		const huidigeInstellingen = this.checkedInstellingen();
		if (huidigeInstellingen.some((search) => search.id === checkedInstelling.id)) {
			this.checkedInstellingen.set(huidigeInstellingen.filter((instelling) => instelling.id !== checkedInstelling.id));
		} else {
			this.checkedInstellingen.set([...huidigeInstellingen, checkedInstelling]);
		}
	}

	selectAllOrNone(allOrNone: boolean) {
		if (allOrNone) {
			this.checkedInstellingen.set(this.filteredInstellingen());
			return;
		}

		this.checkedInstellingen.set([]);
	}
}
